import { removeClass, toggleClass } from "../domUtils";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import { renderAvatarDropdown, refreshAvatarQuota } from "../avatarView";
import { checkClientIntent } from "../authGuards";
import { backFromContextualView, closeModal, openModal, setActiveTab, showContextualView, showView } from "../navigationController";
import { openPaywallWithLayout } from "../commerceController";
import { activateSiblingButton, byId, matchCall } from "../domUtils";
import { applyAiCreateFilter, type AiCreateFilter, runAiSearch, clearAiSearch } from "../aiCreateViewHydrator";
import { modalIds } from "../prototypeData";
import {
	retryAuthTurnstile,
	type AuthTurnstileKind,
} from "../turnstileWidgetController";
import { clearPendingAccessIntent } from '../pendingAccessIntent';
import { clearPendingCheckoutIntent } from '../pendingCheckoutIntent';
import { resetEmailVerificationPendingContext } from '../actions/emailVerificationResendAction';
import { clearAuthModalFields } from '../formProfileController';

export function routeShellAndAuthModals(normalized: string, root: Root, state: PrototypeState, source?: HTMLElement) {
	const changesAuthModal = /\bshow(?:Login|Register|Forgot)Modal\b|\bshowGate(?:Login|Register)\b/.test(normalized);
  if (normalized.includes("toggleAvatarDropdown")) {
    // Single render entry: rewrites both menu containers from
    // `state` alone, so any prior partial DOM is overwritten with
    // the correct content before the dropdown becomes visible.
    renderAvatarDropdown(root, state);
    toggleClass(root, "avatarDropdown", "open");
    // Pro/Max: after the sync card paints (plan-catalog quota fallback),
    // fetch the live remaining/reset and patch the quota ids in place.
    void refreshAvatarQuota(root, state);
  }
  if (normalized.includes("closeAvatarDropdown")) removeClass(root, "avatarDropdown", "open");
  // The paywall free-tier visibility check needs the real viewer
  // state (paid vs visitor). Previously the call hard-coded
  // currentPlan: "visitor" so paid users (lite/pro/max) saw the
  // Free card and the visitor pre-paint copy.
	if (normalized.includes("showPaywall")) {
		const requiredPlan = paidRequiredPlan(matchCall(normalized, 'showPaywall'));
		openPaywallWithLayout(root, state, {
			requiredPlan,
			dynamicRequiredCapability: dynamicPaywallCapabilityFromSource(source),
		});
	}
  if (normalized.includes("closePaywall")) closeModal(root, "paywallModal");
  if (normalized.includes("showLoginModal") || normalized.includes("showGateLogin")) {
    clearAuthModalFields(root, 'login');
    openModal(root, "loginModal");
  }
  if (normalized.includes("closeLoginModal")) {
	if (!changesAuthModal) clearPendingAuthIntents();
    clearAuthModalFields(root, 'login');
    closeModal(root, "loginModal");
  }
  if (normalized.includes("showRegisterModal") || normalized.includes("showGateRegister")) {
    closeModal(root, "loginModal");
    resetEmailVerificationPendingContext(root);
    clearAuthModalFields(root, 'register');
    openModal(root, "registerModal");
  }
  if (normalized.includes("closeRegisterModal")) {
	if (!changesAuthModal) {
		clearPendingAccessIntent();
		const verificationPending = byId(root, 'registerSuccess')?.style.display !== 'none';
		if (!verificationPending) clearPendingCheckoutIntent();
	}
    clearAuthModalFields(root, 'register');
    closeModal(root, "registerModal");
  }
  if (normalized.includes("showForgotModal")) {
    closeModal(root, "loginModal");
    clearAuthModalFields(root, 'forgot');
    openModal(root, "forgotModal");
  }
  if (normalized.includes("closeForgotModal")) {
	if (!changesAuthModal) clearPendingAuthIntents();
    clearAuthModalFields(root, 'forgot');
    closeModal(root, "forgotModal");
  }
	if (normalized.includes("retryAuthTurnstile")) {
		const kind = matchCall(normalized, "retryAuthTurnstile") as AuthTurnstileKind | null;
		const resendVisible = byId(root, 'registerModal')?.classList.contains('active') === true
			&& byId(root, 'registerSuccess')?.style.display !== 'none';
		if (kind === "login" || kind === "register" || kind === "forgot" || kind === 'oauthLink' || kind === 'setPassword' || (kind === 'resend' && resendVisible)) {
			void retryAuthTurnstile(root, state.runtime, kind);
		}
	}

  // Verify-email / reset-password overlay close: hide overlay + go home
  if (normalized.includes("closeVerifyEmailOverlay")) {
    closeModal(root, "verifyEmailOverlay");
    showView("home", root);
  }
  if (normalized.includes("closeResetPasswordOverlay")) {
    closeModal(root, "resetPasswordOverlay");
    showView("home", root);
  }

  if (normalized.includes("closeMermaidFullscreenModal")) {
    closeModal(root, "mermaidFullscreenModal");
  }

  // Generic closeModal('<id>') — whitelisted modalIds only, so an action
  // string can't reach arbitrary DOM. Used by the X buttons in markup that
  // don't have a dedicated named action.
  const closeModalMatch = /closeModal\(['"]([a-zA-Z0-9_-]+)['"]\)/.exec(normalized);
  if (closeModalMatch) {
    const target = closeModalMatch[1];
    if (modalIds.includes(target)) closeModal(root, target);
  }

  // Member Workspace "AI Create" entry. Pro/Max can go straight to the
  // AI Create view; Lite (and below) get routed to the paywall so the
  // CTA stays honest instead of letting Lite land on a feature they
  // cannot actually use.
  if (normalized.includes("openAiCreateFromMember")) {
    const decision = checkClientIntent(state, "aiCreate");
    if (decision.allowed) {
      showView("ai-create", root);
      setActiveTab("ai-create", root);
    } else if (decision.reason === "login") {
      openModal(root, "loginModal");
    } else {
      const requiredPlan = decision.requiredPlan;
      openPaywallWithLayout(root, state, {
        setIntent: (targetRoot) => {
          const slot = targetRoot.querySelector<HTMLElement>("#paywallTargetPlan");
          if (slot) slot.textContent = requiredPlan;
        },
      });
    }
  }

  // AI Create panel filter buttons. The list itself is hydrated by
  // /api/ai/courses (see aiCreateViewHydrator) — this handler just
  // re-runs the hydration with the matching filter.
  if (normalized.includes("filterAiCreate")) {
    const filterValue = matchCall(normalized, "filterAiCreate") ?? "completed";
    const filter: AiCreateFilter =
      filterValue === "inprogress" || filterValue === "completed" ? filterValue : "completed";
    activateSiblingButton(sourceForFilter(root, filter), "category-pill");
    applyAiCreateFilter(root, filter);
  }

  // AI Create search: button-triggered (Enter or click). Sends a fresh
  // server request via fetchAndRenderAiCourses so pagination reflects
  // the full result set, not just the cached page.
  if (normalized.includes("runAiSearch")) {
    runAiSearch(root);
  }
	if (normalized.includes("clearAiSearch")) {
		clearAiSearch(root);
	}

	if (normalized.includes('showHistoryFrom')) {
		showContextualView('history', root);
		setActiveTab('profile', root);
	}
	if (normalized.includes('returnFromHistory')) {
		backFromContextualView(root, { view: 'profile', tab: 'profile' });
	}

  // NOTE: ai-create view hydration on navigation is handled by
  // navigationController.showView(). We deliberately do NOT re-hydrate
  // here — doing so caused duplicate /api/ai/courses requests on every
  // navigation click. The shouldHydrate guard in the hydrator is the
  // last line of defense, but removing this duplicate call is the
  // root fix.
}

/** Action strings are untrusted markup input: only a paid subscription tier is actionable. */
function paidRequiredPlan(value: string | null): 'lite' | 'pro' | 'max' | undefined {
	return value === 'lite' || value === 'pro' || value === 'max' ? value : undefined;
}

/** Only the server-rendered/painter chapter CTA can carry this UX intent. */
function dynamicPaywallCapabilityFromSource(source: HTMLElement | undefined): 'githubDeepDive' | undefined {
	return source?.dataset.requiredCapability === 'githubDeepDive' ? 'githubDeepDive' : undefined;
}

function clearPendingAuthIntents(): void {
	clearPendingAccessIntent();
	clearPendingCheckoutIntent();
}

function sourceForFilter(root: Root, filter: AiCreateFilter): HTMLElement {
  return Array.from(root.querySelectorAll<HTMLElement>("#aiFilterTabs .category-pill"))
    .find((button) => (button.dataset.protoClick ?? "").includes(`filterAiCreate('${filter}'`))
    ?? root.querySelector<HTMLElement>("#aiFilterTabs .category-pill")
    ?? (root as HTMLElement);
}
